// Клик на продукт в „Продадени продукти" → складовата карта на продукта
// (`stockCard/StockCardPanel`), отворена за ПЕРИОДА на справката и на чипа
// „Отчет" — разнасянията, които обясняват реалното. Справката НЕ е по склад,
// картата — е: складът е избор в хедъра на картата и тръгва от склада, от
// който продуктът е изписан най-много през периода (иначе — този от
// конфигурацията); ръчният избор важи за продукта, ↑/↓ към друг продукт
// тръгва пак от неговия склад. Над табовете — лента „По рецепта · Разнесено ·
// Разлика" с разбивката по складове и замените.
import { useMemo, useState } from "react";
import { Select } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  useInventorySettings,
  useProducts,
  useStockBalances,
  useWarehouses,
} from "../../../../hooks/useInventory";
import { formatMoney } from "../../components/numbers";
import { clampDecimals, formatQty } from "../../components/qty";
import { EmptyScreen } from "../../components/list/ListBits";
import { formatPeriod, type Period } from "../../components/period/periodPresets";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";
import { stockRowOf, type StockRow } from "../components/stockShared";
import { StockCardPanel } from "../stockCard/StockCardPanel";
import { SignedMoney, SignedQuantity } from "./SoldVarianceBits";
import { cardWarehouseOf, substitutionLines, type SoldRow } from "./soldVarianceModel";

type Props = {
  row: SoldRow;
  period: Period;
  variant: "pane" | "sheet";
  decimals: number;
  onClose: () => void;
};

// Продукт извън списъка (изтрит) — картата пак се отваря, наличността е 0
const fallbackStockRow = (row: SoldRow, decimals: number): StockRow => ({
  productId: row.sellableId,
  name: row.name,
  unit: row.unit,
  category: null,
  quantity: 0,
  decimals: clampDecimals(decimals),
  minQty: null,
  wastePct: null,
  weightedAvgPrice: null,
  lastDeliveryPrice: null,
  vatRate: 0,
  sumNoVat: 0,
  sumWithVat: 0,
  bucket: "over",
});

export const SoldProductPanel = ({ row, period, variant, decimals, onClose }: Props) => {
  const { t } = useTranslation();
  const { warehouseId: configuredWarehouseId } = useInventoryShell();
  const { data: warehouses = [] } = useWarehouses();
  const [chosen, setChosen] = useState<{ productId: number; warehouseId: number } | null>(null);

  const warehouseId =
    chosen?.productId === row.sellableId
      ? chosen.warehouseId
      : cardWarehouseOf(
          row.actual,
          configuredWarehouseId,
          warehouses.map((warehouse) => warehouse.id),
        );
  const warehouseName = warehouses.find((warehouse) => warehouse.id === warehouseId)?.name ?? "";

  // Редът на картата (наличност, стойност, минимум) — както в „Наличности"
  const { data: products = [] } = useProducts();
  const { data: balances = [] } = useStockBalances(warehouseId);
  const { data: settings } = useInventorySettings();
  const vatRegistered = settings?.vatRegistered ?? true;
  const stockRow = useMemo(() => {
    const product = products.find((item) => item.id === row.sellableId);
    if (!product) return fallbackStockRow(row, decimals);
    const balance = balances.find((item) => item.productId === row.sellableId);
    // Един ред и за изтрит продукт без наличност в склада (buildStockRows би го изпуснал)
    return stockRowOf(product, balance, vatRegistered);
  }, [products, balances, vatRegistered, row, decimals]);

  const postedIn = new Map((row.actual?.warehouses ?? []).map((item) => [item.warehouseId, item.quantity]));
  const unit = row.unit ? ` ${row.unit}` : "";
  const picker = (
    <Select
      size="small"
      variant="borderless"
      value={warehouseId ?? undefined}
      onChange={(value: number) => setChosen({ productId: row.sellableId, warehouseId: value })}
      aria-label={t("inventoryPage.fieldWarehouse")}
      popupMatchSelectWidth={false}
      options={warehouses.map((warehouse) => ({
        value: warehouse.id,
        label: postedIn.has(warehouse.id)
          ? `${warehouse.name} · ${formatQty(postedIn.get(warehouse.id) ?? 0, decimals)}${unit}`
          : warehouse.name,
      }))}
      className="min-w-0 max-w-[260px] [&_.ant-select-selection-item]:!text-[12.5px] [&_.ant-select-selection-item]:!font-semibold [&_.ant-select-selector]:!px-1"
    />
  );

  const summary = <SoldProductSummary row={row} period={period} decimals={decimals} warehouses={warehouses} />;

  return (
    <div data-sold-panel className="flex h-full min-h-0 flex-1 flex-col">
      {warehouseId == null ? (
        <EmptyScreen Icon={InboxOutlined} tone="brand" title={t("inventoryPage.stockPickWarehouse")} />
      ) : (
        <StockCardPanel
          // Нов период на справката → картата тръгва наново от него
          key={formatPeriod(period)}
          row={stockRow}
          warehouseId={warehouseId}
          warehouseName={warehouseName}
          variant={variant}
          onClose={onClose}
          initialPeriod={period}
          initialMovementType="report"
          warehousePicker={picker}
          backLabel={t("inventoryPage.soldProducts")}
          summary={summary}
        />
      )}
    </div>
  );
};

// ─── Лентата „По рецепта · Разнесено · Разлика" над табовете ──────────────
type SummaryProps = {
  row: SoldRow;
  period: Period;
  decimals: number;
  warehouses: { id: number; name: string }[];
};

const SoldProductSummary = ({ row, period, decimals, warehouses }: SummaryProps) => {
  const { t } = useTranslation();
  const unit = row.unit ?? "";
  const qty = (value: number) => `${formatQty(value, decimals)}${unit ? ` ${unit}` : ""}`;
  const nameOf = (warehouseId: number) =>
    warehouses.find((warehouse) => warehouse.id === warehouseId)?.name ?? `#${warehouseId}`;
  const byWarehouse = (row.actual?.warehouses ?? []).filter((item) => item.quantity > 0);
  const substitutions = substitutionLines(row.actual, decimals, row.unit, t);
  const cell = "flex min-w-0 flex-col gap-0.5";
  const label = "text-[10.5px] font-semibold uppercase leading-[normal] tracking-[0.1em] text-muted";

  return (
    <div className="flex shrink-0 flex-col gap-2 border-b border-shade-2 bg-fill px-4 py-2.5">
      <div className="grid grid-cols-3 gap-3">
        <div className={cell}>
          <span className={label}>{t("inventoryPage.sdGroupRecipe")}</span>
          <span className="font-sheet text-[13.5px] font-semibold tabular-nums text-ink">{qty(row.quantity)}</span>
          <span className="font-sheet text-xs tabular-nums text-body">{formatMoney(row.totalSum)}</span>
        </div>
        <div className={cell}>
          <span className={label}>{t("inventoryPage.sdGroupPosted")}</span>
          <span className="font-sheet text-[13.5px] font-semibold tabular-nums text-ink">
            {row.actual ? qty(row.actual.quantity) : "…"}
          </span>
          <span className="font-sheet text-xs tabular-nums text-body">
            {row.actual ? formatMoney(row.actual.valueWithVat) : "…"}
          </span>
        </div>
        <div className={cell}>
          <span className={label}>{t("inventoryPage.sdGroupDiff")}</span>
          <span className="text-[13.5px]">
            <SignedQuantity value={row.variance?.quantity ?? null} decimals={decimals} unit={row.unit} />
          </span>
          <span className="text-xs">
            <SignedMoney value={row.variance?.valueWithVat ?? null} />
          </span>
        </div>
      </div>
      {(byWarehouse.length > 1 || substitutions.length > 0) && (
        <div className="flex flex-col gap-0.5 text-[12px] leading-[1.4] text-body">
          {byWarehouse.length > 1 && (
            <span>
              {t("inventoryPage.sdPostedByWarehouse", {
                list: byWarehouse.map((item) => `${nameOf(item.warehouseId)} ${qty(item.quantity)}`).join(" · "),
              })}
            </span>
          )}
          {substitutions.map((line) => (
            <span key={line}>⇄ {line}</span>
          ))}
        </div>
      )}
      <span className="text-[11.5px] leading-[1.4] text-muted">
        {t("inventoryPage.sdCardPeriodNote", { period: formatPeriod(period) })}
      </span>
    </div>
  );
};

export default SoldProductPanel;
